import React from 'react';
import { useTranslation } from 'react-i18next';
import '../styles.scss'
import { CardInfo } from '../../../Portfolio_R/SummaryCharts/components/CardInfo';
import SmallTable from '../components/SmallTable/SmallTable';
import LinearProjectReport from '../components/LinearProjectReport/LinearProjectReport';
import { useNumberFormatter } from '../../../../hooks/useNumberFormatter';
import FinancialAdvanceProgress from '../components/FinancialAdvanceProgress/FinancialAdvanceProgress';
import ExecutionProgress from '../components/ExecutionProgress/ExecutionProgress';
import MilestonesAlert from '../components/MilestoneAlerts/MilestonesAlert';
import NomenclatureItem from '../components/NomenclatureItem/NomenclatureItem';
import { HeaderTCDI } from '../components/HeaderTCDI/HeaderTCDI';
import { capitalizeFirstLetter } from '../../../../services/stringUtil';
import { dataProgress } from './Common/NomenclatureItems';
import { EXTENDED_REPORT_KEY } from '../utils/Constants/ProjectReportConstants';
import { mapGeneralInfoInDataSource } from '../utils/Helpers/MapperHelper';
import { NoContentView } from '../components/NoContentView/NoContentView';

const TCDIProjectSummary = ({ data }) => {
    const { t } = useTranslation();
    const { formatNumber } = useNumberFormatter();
    const divisorView = 1000000;
    const dataSource = data.typeReport === EXTENDED_REPORT_KEY ? mapGeneralInfoInDataSource(data) : data;
    dataSource.baseInformation = data.typeReport !== EXTENDED_REPORT_KEY ? dataSource.baseInformacion : dataSource.baseInformation;
    dataSource.projectMilestones = data.typeReport !== EXTENDED_REPORT_KEY ? dataSource.projectMilestones : dataSource.milestones;
    dataSource.financialInformation = data.typeReport !== EXTENDED_REPORT_KEY ? dataSource.financialinformation : dataSource.financialInformation;

    const { baseInformation, projectMilestones, financialInformation } = dataSource;

    return (
        <section id="report-main" className="c-main">
            <HeaderTCDI data={dataSource} />

            <article className={dataSource.typeReport !== EXTENDED_REPORT_KEY ? "content-project-summary" : "content-extended-summary"}>
                <div className="objectives-project-summary">
                    <h3 className="chart-title">{t('objective')}</h3>
                    <p className="objective-text-tcdi">{dataSource.objectiveName}</p>
                </div>

                <div id='basic-info-project-summary' className='basic-info-project-summary'>
                    <h3 className="chart-title">{t('one_pager_report.base_info')}</h3>

                    <h3 className='counterpart-proyect-subtitle'>{capitalizeFirstLetter(t('executed_by'))}</h3>
                    <p>{t(baseInformation.executedBy) || 'N/A'}</p>

                    <h3 className='counterpart-proyect-subtitle'>{t('executor')}</h3>
                    <p>{baseInformation.executingAgency || 'N/A'}</p>

                    <h3 className='counterpart-proyect-subtitle'>{t('role_id_2') + ' ' + t('bid')}</h3>
                    <p>{baseInformation.bidTeamLeader || 'N/A'}</p>

                    <h3 className='counterpart-proyect-subtitle'>{t('udr')}</h3>
                    <p>{baseInformation.udr || 'N/A'}</p>

                    <h3 className='counterpart-proyect-subtitle'>{t('taxonomy')}</h3>
                    <p>{baseInformation.nameTaxonomy || 'N/A'}</p>

                    {
                        baseInformation.participatingCountries &&
                        <>
                            <h3 className='counterpart-proyect-subtitle'>{capitalizeFirstLetter(t('participing_countries'))}</h3>
                            <p>{baseInformation.participatingCountries || 'N/A'}</p>
                        </>
                    }
                </div>

                <div 
                    id={dataSource.typeReport !== EXTENDED_REPORT_KEY ? 'project-milestone-project-summary' : 'project-milestone-extended-summary'} 
                    className={dataSource.typeReport !== EXTENDED_REPORT_KEY ? 'project-milestone-project-summary' : 'project-milestone-extended-summary'}>
                    <div className="c-project-milestone">
                        <div style={dataSource.typeReport !== EXTENDED_REPORT_KEY ? {width: '60%'} : {}}>
                            <h3 className="chart-title">{t('one_pager_report.project_milestones')}</h3>
                            <div className="chart-content">
                                <CardInfo title={t('approval')} data={projectMilestones.dateApproval} />
                                <CardInfo title={t('effectiveness')} data={projectMilestones.dateEffectiveness} />
                                <CardInfo title={t('eligibility')} data={projectMilestones.dateEligibility} />
                                <CardInfo title={t('first_disbursement')} data={projectMilestones.dateFirstDisbursement} />
                                <CardInfo title={t('last_disbursement')} data={projectMilestones.dateLastDisbursement ? `${projectMilestones.dateLastDisbursement} (${capitalizeFirstLetter(t('original'))})` : '-'} />
                                <CardInfo title={`${t('extension')}: ${projectMilestones.monthExtension || 0} ${t('one_pager_report.months')}`} data={`${projectMilestones.dateExtension} (${capitalizeFirstLetter(t('current'))})`} />
                            </div>
                        </div>
                        <div>
                            <h3 className="chart-title">{capitalizeFirstLetter(t('temporary_advance_title').toLowerCase())}</h3>
                            <ExecutionProgress
                                completed={projectMilestones.executionPlan.elapsedPercent}
                                totalTime={projectMilestones.executionPlan.totalTime}
                                initialMonths={projectMilestones.executionPlan.legalValidityMounth}
                                initialDate={projectMilestones.executionPlan.datelegalValidity}
                                pendingMonths={projectMilestones.executionPlan.lastCurrentDisbursementPeriodMounth}
                                endingDate={projectMilestones.executionPlan.datelastCurrentDisbursementPeriod} />
                        </div>
                    </div>
                    <div className='description'>
                        <p>{capitalizeFirstLetter(t('number_of_extensions_ct'))}: <b>{projectMilestones.monthExtension}</b></p>
                        <p>{capitalizeFirstLetter(t('active_alerts'))}: <b>{projectMilestones.dgmAlerts.length}</b></p>
                        {projectMilestones.dgmAlerts.map(item => <MilestonesAlert alert={item} />)}
                    </div>

                </div>

                {
                    dataSource.typeReport !== EXTENDED_REPORT_KEY && (
                        <div id='financial-info-project-summary' className="financial-info-project-summary">
                            <h3 className="chart-title">{t('info_financial')}</h3>
                            <div className="c-financial-info">
                                <div id='financial_info_content' className="current-amount">
                                    <span>{t('one_pager_report.current_balances')}</span>
                                    <SmallTable
                                        showHeaders
                                        itemTitle={t('bid')}
                                        original={formatNumber((financialInformation.originalAmountBID / divisorView))}
                                        cancel={formatNumber((financialInformation.cancelledAmountBID / divisorView))}
                                        adjusted={formatNumber((financialInformation.adjustedAmountBID / divisorView))}
                                        current={formatNumber((financialInformation.currentBID / divisorView))}
                                    />

                                    <div>
                                        <span>{t('one_pager_report.financial_advance')}</span>
                                        <br />
                                        <span>{t('approved_current_amount')} {formatNumber(financialInformation.financialAdvance.currentApprovedAmount)} {t('dolar_sign')}</span>
                                    </div>

                                    <FinancialAdvanceProgress
                                        disbursmentAmount={financialInformation.financialAdvance.disbursedAmount}
                                        disbursment={financialInformation.financialAdvance.disbursedPercent}
                                        committedAmount={financialInformation.financialAdvance.committedAmount}
                                        committed={financialInformation.financialAdvance.committedPercent}
                                        pendignAmount={financialInformation.financialAdvance.pendingAmount}
                                        pending={financialInformation.financialAdvance.pendingPercent} />

                                    <div>
                                        <span>{t('disbursements')}</span>
                                        <div className="chart-content">
                                            <CardInfo
                                                title={t('gcm_no_disbursements_one_year')}
                                                data={financialInformation.financialAdvance.lastDisbursementDate} />
                                            <CardInfo
                                                title={t('min_justify_new_disbursement')}
                                                data={formatNumber(financialInformation.financialAdvance.minimumJustifyAdvance)}
                                                units={t('dolar_sign')} />
                                        </div>
                                    </div>
                                </div>

                                <div className="component-progress">
                                    <div className='c-title-nom'>
                                        <span>{t('one_pager_report.advance_per_component')}</span>
                                        <NomenclatureItem data={dataProgress} />
                                    </div>
                                    {
                                        financialInformation.advanceComponent.length ? financialInformation.advanceComponent.slice(0,6).map(item => (
                                            <div className="component-advance-content">
                                                <LinearProjectReport
                                                    title={item.nameComponent}
                                                    completed={item.justifiedPercent}
                                                    pending={item.pendingPercent}
                                                    initialAmount={'$ ' + formatNumber(item.justifiedAmount)}
                                                    pendingAmount={'$ ' + formatNumber(item.pendingAmount)} />
                                            </div>
                                        ))
                                        : <NoContentView title={t('last_no_information')} />
                                    }
                                </div>
                            </div>
                        </div>
                    )
                }
            </article>
        </section>
    )
}

export default TCDIProjectSummary